import { canUseLocalStorage } from '@ps/web-core-utils';
import type { TrackedTranslation } from '@ps/web-shared-components';
import { isString } from 'lodash';

import type { GamesByClubs, HGLocalStorage, HomeGamesError, PageData } from '../types/common';
import { ClubPlayerRole, UserClubRole } from '../types/common';

import {
  TXTCLI_Admin,
  TXTCLI_Guest,
  TXTCLI_Manager,
  TXTCLI_Member,
  TXTCLI_Observer,
  TXTCLI_Suspended,
  TXTWCL_Waiting_for_Confirmation
} from './constants.messages.json';

export type SetOnboardingFlowStep = React.Dispatch<React.SetStateAction<Element | null>> | null;

export const PKR_INITIAL_GAMES_DATA: GamesByClubs = {};
export const PKR_INITIAL_TOURNAMENT_DATA: GamesByClubs = {};

export const PKR_LOADERS = {
  joinPromoClub: 'joinPromoClub',
  userClubs: 'userClubs',
  clubInfo: 'clubInfo',
  clubOptionsById: (clubId: string | number) => `clubOptionsById-${clubId}`,
  userClubsAndGames: 'userClubsAndGames',
  registeredTournaments: 'registeredTournaments',
  scheduledTables: 'scheduledTables',
  playersByClubId: (clubId: string | number) => `playersByClubId-${clubId}`,
  playerTournamentResults: 'playerTournamentResults',
  clubMembers: 'clubMembers',
  clubSeasons: 'clubSeasons',
  clubSeasonStats: 'clubSeasonStats',
  clubSeasonSummaries: 'clubSeasonSummaries',
  tableStructures: 'tableStructures',
  tournamentStructures: 'tournamentStructures',
  initial: 'initial'
};

export const PKR_INITIAL_ERROR: HomeGamesError = {
  critical: false,
  info: false,
  major: false,
  minor: false
};

export const PKR_LOCAL_STORAGE = 'homegames';
export const PKR_INITIAL_LOCAL_STORAGE = {
  gameTileView: {},
  onboarding: {}
};

export const PKR_INITIAL_STATE: PageData = {
  announcement: null,
  clubs: [],
  games: {
    tables: [],
    tourns: [],
    recentTourns: [],
    registeredTourns: [],
    scheduledTables: [],
    selectedGameIds: []
  },
  clubOptionsById: {},
  playersByClubId: {},
  playerTournamentResults: [],
  serviceStatusData: [],
  clubMembers: { approved: [], pending: [], blocked: [] },
  clubSeasonsById: {},
  clubSeasonStatsById: {},
  clubRecentTournamentSummary: [],
  error: PKR_INITIAL_ERROR,
  loading: {
    [PKR_LOADERS.initial]: true,
    [PKR_LOADERS.clubInfo]: true,
    [PKR_LOADERS.clubSeasonSummaries]: true,
    [PKR_LOADERS.clubSeasonStats]: true
  },
  uiLayout: {
    clubNavHidden: false,
    standingsPlayMoneyFlag: false
  },
  tableStructures: {
    tableParams: []
  },
  localStorage:
    canUseLocalStorage && isString(window.localStorage.getItem(PKR_LOCAL_STORAGE))
      ? (JSON.parse(
          window.localStorage.getItem(PKR_LOCAL_STORAGE) ??
            JSON.stringify(PKR_INITIAL_LOCAL_STORAGE)
        ) as HGLocalStorage)
      : PKR_INITIAL_LOCAL_STORAGE,
  wallPosts: [],
  wallRanks: []
};

export const PKR_ROLE_TRANSLATIONS = {
  [UserClubRole.Manager]: TXTCLI_Manager,
  [UserClubRole.Admin]: TXTCLI_Admin,
  [UserClubRole.Member]: TXTCLI_Member,
  [UserClubRole.Observer]: TXTCLI_Observer,
  [UserClubRole.Guest]: TXTCLI_Guest,
  [UserClubRole.Confirmation]: TXTWCL_Waiting_for_Confirmation,
  [UserClubRole.Suspended]: TXTCLI_Suspended
};

export const PKR_USER_ROLE_TRANSLATIONS = {
  [ClubPlayerRole.FOUNDER]: TXTCLI_Manager,
  [ClubPlayerRole.ADMIN]: TXTCLI_Admin,
  [ClubPlayerRole.MEMBER]: TXTCLI_Member,
  [ClubPlayerRole.OBSERVER]: TXTCLI_Observer,
  [ClubPlayerRole.GUEST]: TXTCLI_Guest,
  [ClubPlayerRole.APPLIED]: TXTWCL_Waiting_for_Confirmation,
  [ClubPlayerRole.SUSPENDED]: TXTCLI_Suspended
};

export const PKR_AVATAR_COUNT = 4;

export const PKR_CLUB_NAV_ID = 'homegames-club-nav';

export const PKR_RESTRICTED_SYMBOLS = /<|>|\{|\}|\(|\)|\[|\]/;
export const PKR_RESTRICTED_SYMBOLS_STRING = PKR_RESTRICTED_SYMBOLS.source.replace(/[|\\]/g, ' ');
export const PKR_MIN_SYMBOLS_TOURN_NAME = 6;
export const PKR_MAX_SYMBOLS_TOURN_NAME = 30;

export const PKR_RETRY_INTERVALS = [1000, 3000, 6000];

export const PKR_DEFAULT_HOLDEM_GAME_CONFIG = {
  handType: 'HT_HOLDEM',
  potStruct: 'STRUCT_NO_LIMIT',
  tableSize: 6
};

export const MIN_DESKTOP_CLIENT_WIDTH = 910;

const SECOND = 1000;
export const MINUTE: number = 60 * SECOND;

export const PLAYERS_STATUS_LIMIT = 200;

export const TOURNFLAG_SERVINT_RECURRING_TOURN = 0x10000; // [MJ 2011-02-27] #17813

export const PKR_USERS_IN_LEADER_BOARD_NUMBER = 3;

export const matchNumbersWithOrWithoutCommasRegExp = /(\d{1,3}(,\d{3})*|\d+)/;

export const THOUSAND = 1000;
export const MILLION = 1000000;
export const BILLION = 1000000000;

export interface NavigationTab<T extends string> {
  title: TrackedTranslation;
  shortTitle: TrackedTranslation;
  tabActiveStyle?: string;
  tabName: T;
  animation?: React.FC;
  animationStyle?: string;
}

export interface NavigationProperties<T extends string> {
  navTabs?: NavigationTab<T>[];
  selectedTab: T;
  onSelectTab: (page: T) => void;
  infoRef?: SetOnboardingFlowStep;
  className?: string;
}

export const TIME = {
  SECOND: 1000,
  MINUTE: 1000 * 60,
  HOUR: 1000 * 60 * 60,
  DAY: 1000 * 60 * 60 * 24,
  WEEK: 1000 * 60 * 60 * 24 * 7,
  TWO_WEEKS: 1000 * 60 * 60 * 24 * 7 * 2,
  MONTH: 1000 * 60 * 60 * 24 * 30,
  YEAR: 1000 * 60 * 60 * 24 * 365
};

export const TIME_IN_SECOND = {
  SECOND: 1,
  MINUTE: 60,
  HOUR: 60 * 60,
  DAY: 60 * 60 * 24,
  WEEK: 60 * 60 * 24 * 7,
  TWO_WEEKS: 60 * 60 * 24 * 7 * 2,
  MONTH: 60 * 60 * 24 * 30,
  YEAR: 60 * 60 * 24 * 365
};

export const EMBEDDED_RESOLVER_PREFIX = 'pokerstars://resolve/';
